import { macro } from '../data/macro'
import type { MacroSeries, YieldPoint } from '../data/macro'

const TREND_ICON = { up: '▲', down: '▼', flat: '→' }
const TREND_COLOR = {
  up:   'var(--pos)',
  down: 'var(--neg)',
  flat: 'var(--text-2)',
}

function SectionHead({ label, title, asOf }: { label: string; title: string; asOf?: string }) {
  return (
    <div className="flex items-center gap-3 mb-4 mt-8 first:mt-0">
      <span className="font-mono text-[9px] font-bold tracking-[0.18em] text-accent">{label}</span>
      <h2 className="text-[17px] font-semibold text-t1">{title}</h2>
      <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
      {asOf && <span className="font-mono text-[9px] text-t3 tabular-nums shrink-0">{asOf}</span>}
    </div>
  )
}

function SeriesCard({ s }: { s: MacroSeries }) {
  return (
    <div className="card p-4 flex flex-col gap-1.5">
      <span className="font-mono text-[9px] text-t3 tracking-widest uppercase">{s.label}</span>
      <div className="flex items-baseline gap-2">
        <span className="stat-value text-[24px] text-t1">{s.value}</span>
        <span className="font-mono text-[11px] text-t3">{s.unit}</span>
        <span className="font-mono text-[12px] font-bold ml-auto" style={{ color: TREND_COLOR[s.trend] }}>
          {TREND_ICON[s.trend]}
        </span>
      </div>
      <div className="flex justify-between font-mono text-[9px] text-t3 mt-auto pt-2" style={{ borderTop: '1px solid var(--border)' }}>
        <span>{s.source}</span>
        <span className="tabular-nums">{s.asOf}</span>
      </div>
    </div>
  )
}

function YieldCurveChart({ label, points }: { label: string; points: YieldPoint[] }) {
  const maxY = Math.max(...points.map(p => p.yield))
  const minY = Math.min(...points.map(p => p.yield))
  const range = Math.max(maxY - minY, 0.1)
  const h = 80
  const w = 100
  const step = w / (points.length - 1)
  const isInverted = points[0].yield > points[points.length - 1].yield

  const toSvg = (val: number) => h - ((val - minY) / range) * (h - 10) - 2

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[11px] font-bold text-t1">{label}</span>
        {isInverted && (
          <span className="badge" style={{ color: 'var(--neg)', background: 'var(--neg-bg)', borderColor: 'var(--neg)30' }}>
            Invertiert
          </span>
        )}
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: 80 }} aria-hidden="true">
        <polyline
          points={points.map((p, i) => `${i * step},${toSvg(p.yield)}`).join(' ')}
          fill="none"
          stroke={isInverted ? 'var(--neg)' : 'var(--accent)'}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        {points.map((p, i) => (
          <circle key={i} cx={i * step} cy={toSvg(p.yield)} r="2.5" fill={isInverted ? 'var(--neg)' : 'var(--accent)'} />
        ))}
      </svg>
      <div className="flex justify-between mt-2">
        {points.map(p => (
          <div key={p.maturity} className="flex flex-col items-center gap-0.5">
            <span className="font-mono text-[10px] text-t1 tabular-nums">{p.yield.toFixed(2)}</span>
            <span className="font-mono text-[8px] text-t3">{p.maturity}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function Makro() {
  const m = macro

  return (
    <main className="max-w-[1200px] mx-auto px-4 py-8">

      {/* Leitzinsen */}
      <SectionHead label="01" title="Leitzinsen" asOf={m.updatedAt} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
        {m.rates.map(r => (
          <div key={r.label} className="card p-4 flex flex-col gap-2">
            <span className="font-mono text-[9px] text-t3 tracking-widest uppercase">{r.label}</span>
            <div className="flex items-baseline gap-2">
              <span className="stat-value text-[28px] text-t1">{r.value.toFixed(2)}</span>
              <span className="font-mono text-[11px] text-t3">%</span>
              <span
                className="font-mono text-[11px] font-bold ml-auto tabular-nums"
                style={{ color: r.change < 0 ? 'var(--neg)' : r.change > 0 ? 'var(--pos)' : 'var(--text-2)' }}
              >
                {r.change > 0 ? '+' : ''}{r.change} bp
              </span>
            </div>
            <p className="font-mono text-[10px] text-t2">{r.expectation}</p>
            <div className="flex justify-between font-mono text-[9px] text-t3 mt-auto pt-2" style={{ borderTop: '1px solid var(--border)' }}>
              <span>Nächste Sitzung</span>
              <span className="tabular-nums">{r.nextMeeting}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Zinskurven */}
      <SectionHead label="02" title="Zinskurven" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <YieldCurveChart label="USA" points={m.yieldCurve.us} />
        <YieldCurveChart label="Eurozone" points={m.yieldCurve.eu} />
      </div>

      {/* Inflation */}
      <SectionHead label="03" title="Inflation" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {m.inflation.map(s => <SeriesCard key={s.label} s={s} />)}
      </div>

      {/* Wachstum */}
      <SectionHead label="04" title="Wirtschaftswachstum (BIP)" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {m.growth.map(s => <SeriesCard key={s.label} s={s} />)}
      </div>

      {/* Arbeitsmarkt */}
      <SectionHead label="05" title="Arbeitsmarkt" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {m.labor.map(s => <SeriesCard key={s.label} s={s} />)}
      </div>

      {/* Kalender */}
      <SectionHead label="06" title="Nächste Ereignisse" />
      <div className="card overflow-hidden">
        <table className="w-full text-[12px]">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th className="font-mono text-[9px] text-t3 tracking-widest text-left px-4 py-2.5">DATUM</th>
              <th className="font-mono text-[9px] text-t3 tracking-widest text-left px-4 py-2.5">EREIGNIS</th>
              <th className="font-mono text-[9px] text-t3 tracking-widest text-left px-4 py-2.5 hidden sm:table-cell">REGION</th>
            </tr>
          </thead>
          <tbody>
            {m.nextEvents.map((ev, i) => (
              <tr
                key={i}
                style={{
                  borderBottom: i < m.nextEvents.length - 1 ? '1px solid var(--border)' : undefined,
                  background: ev.importance === 'high' ? 'rgba(255,176,32,0.03)' : undefined,
                }}
              >
                <td className="font-mono text-[11px] text-t2 px-4 py-3 tabular-nums whitespace-nowrap">{ev.date}</td>
                <td className="font-semibold text-t1 px-4 py-3">
                  {ev.importance === 'high' && (
                    <span className="font-mono text-[8px] text-warn mr-1.5">●</span>
                  )}
                  {ev.label}
                </td>
                <td className="font-mono text-[10px] text-t3 px-4 py-3 hidden sm:table-cell">{ev.region}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </main>
  )
}
